/**
 * Offline Sync Engine – TGAone
 * Auto-syncs queued actions when connection returns.
 * Server = finale Wahrheit. Last-write-wins conflict resolution.
 */
import {
  getQueueItems, updateQueueItem, removeQueueItem,
  clearCompletedQueue, getPendingPhotos, removePhoto,
} from "./offlineDB";

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 2000;

let syncing = false;
let listeners = new Set();

// ── Event system ──
export function onSyncEvent(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(event, data) {
  listeners.forEach((fn) => fn(event, data));
}

// ── Core sync ──
export async function processQueue() {
  if (syncing || !navigator.onLine) return;
  syncing = true;
  emit("sync-start", null);

  try {
    // 1. Process mutation queue (chronological order)
    const items = await getQueueItems("pending");
    items.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

    for (const item of items) {
      await updateQueueItem(item.id, { status: "syncing" });
      emit("sync-progress", { id: item.id, type: item.type });

      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        if (item.method !== "FORMDATA") {
          headers["Content-Type"] = "application/json";
        }

        let fetchOpts = { method: item.method, headers };
        if (item.body && item.method !== "GET") {
          fetchOpts.body = typeof item.body === "string" ? item.body : JSON.stringify(item.body);
        }

        const res = await fetch(item.endpoint, fetchOpts);

        if (res.ok) {
          await removeQueueItem(item.id);
          emit("sync-item-done", { id: item.id, type: item.type, success: true });
        } else if (res.status === 409 || res.status === 400) {
          // Conflict or validation error – remove from queue, server is truth
          let detail = "";
          try { const errData = await res.json(); detail = errData.detail || ""; } catch {}
          await removeQueueItem(item.id);
          emit("sync-conflict", {
            id: item.id,
            type: item.type,
            detail,
            endpoint: item.endpoint,
            method: item.method,
          });
          emit("sync-item-done", { id: item.id, type: item.type, success: false, conflict: true });
        } else {
          throw new Error(`HTTP ${res.status}`);
        }
      } catch (err) {
        const retries = (item.retries || 0) + 1;
        if (retries >= MAX_RETRIES) {
          await updateQueueItem(item.id, { status: "failed", retries, error: err.message });
          emit("sync-item-failed", { id: item.id, type: item.type, error: err.message });
        } else {
          await updateQueueItem(item.id, { status: "pending", retries });
        }
      }
    }

    // 2. Process offline photos
    const photos = await getPendingPhotos();
    for (const photo of photos) {
      try {
        const token = localStorage.getItem("token");
        const fd = new FormData();
        fd.append("file", photo.blob, photo.filename);

        const res = await fetch(
          `/api/projects/${photo.projectId}/tasks/${photo.taskId}/photos`,
          { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd }
        );

        if (res.ok || res.status === 400 || res.status === 409) {
          await removePhoto(photo.id);
          emit("sync-photo-done", { id: photo.id, success: res.ok });
        }
      } catch {
        // Will retry on next sync
      }
    }

    await clearCompletedQueue();
    emit("sync-complete", null);
  } catch (err) {
    emit("sync-error", { error: err.message });
  } finally {
    syncing = false;
  }
}

// ── Auto-sync on reconnect ──
let initialized = false;

export function initOfflineSync() {
  if (initialized) return;
  initialized = true;

  window.addEventListener("online", () => {
    emit("online", null);
    // Small delay to let connection stabilize
    setTimeout(() => processQueue(), RETRY_DELAY_MS);
  });

  window.addEventListener("offline", () => {
    emit("offline", null);
  });

  // Periodic sync every 30s when online
  setInterval(() => {
    if (navigator.onLine) processQueue();
  }, 30000);

  // Initial sync on load
  if (navigator.onLine) {
    setTimeout(() => processQueue(), 3000);
  }
}

export function isSyncing() {
  return syncing;
}
